// src/pages/ContractorDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE from "../api/apiBase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { auth, db } from "../services/firebase";
import "./ContractorDashboard.css";

const LOCAL_STORAGE_KEY = "measuresmart_projects";

// ✅ Firestore collections (adjust if your org collection name differs)
const USERS_COLLECTION = "users";
const ORG_COLLECTION_CANDIDATES = [
  "contractorOrganizations",  // ✅ correct collection (must be first)
  "contractorOrgs",
  "orgs",
  "organizations"
];
// ✅ Backend API base (for Stripe checkout/session creation later)
/* ----------------------------- small utilities ----------------------------- */

const safeParse = (json, fallback) => {
  try {
    return JSON.parse(json);
  } catch {
    return fallback;
  }
};

const toFiniteNumber = (v) => {
  const n = typeof v === "string" ? Number(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
};

const toMillis = (ts) => {
  if (!ts) return 0;

  // Firestore Timestamp
  if (typeof ts?.toDate === "function") {
    try {
      return ts.toDate().getTime();
    } catch {
      return 0;
    }
  }

  // Timestamp-like object
  if (typeof ts === "object") {
    if (typeof ts.seconds === "number") return ts.seconds * 1000;
    if (typeof ts._seconds === "number") return ts._seconds * 1000;
  }

  // ISO/string/date
  try {
    const n = new Date(ts).getTime();
    return Number.isNaN(n) ? 0 : n;
  } catch {
    return 0;
  }
};

const safeLower = (v) => String(v || "").toLowerCase();

const prettyStatus = (s) => {
  const v = safeLower(s);
  if (!v) return "inactive";
  if (v === "active") return "active";
  if (v === "trialing") return "trialing";
  if (v === "past_due") return "past due";
  if (v === "canceled") return "canceled";
  if (v === "incomplete") return "incomplete";
  return v.replace(/_/g, " ");
};

/* --------------------------- type / labels / images --------------------------- */

const getProjectType = (p) => {
  if (!p) return "room";

  // prefer explicit fields
  const pt = safeLower(p.projectType);
  const mode = safeLower(p.mode);

  if (pt === "surface" || mode === "surface") return "surface";
  if (pt === "multiroom" || mode === "multiroom") return "multiroom";

  // inference
  if (Array.isArray(p.rooms)) return "multiroom";
  if (p.surfaceUrl) return "surface";

  return "room";
};

const typeLabel = (p) => {
  const t = getProjectType(p);
  if (t === "surface") return "Surface";
  if (t === "multiroom") return "MultiRoom";
  return "Room";
};

const nameLabel = (p) => {
  // Always prefer the true project name, with a safe fallback.
  return p?.projectName || `Project ${String(p?.id || "").slice(-8)}` || "—";
};

const getTopLevelImages = (p) => {
  const imgs = [];
  if (p.surfaceUrl) imgs.push({ url: p.surfaceUrl, alt: "Surface" });
  if (p.widthUrl) imgs.push({ url: p.widthUrl, alt: "Width" });
  if (p.lengthUrl) imgs.push({ url: p.lengthUrl, alt: "Length" });
  if (p.widthImageUrl) imgs.push({ url: p.widthImageUrl, alt: "Width" });
  if (p.lengthImageUrl) imgs.push({ url: p.lengthImageUrl, alt: "Length" });

  const seen = new Set();
  return imgs.filter((x) => x.url && !seen.has(x.url) && seen.add(x.url));
};

const getSingleRoom = (p) => {
  const rooms = Array.isArray(p?.rooms) ? p.rooms : [];
  return rooms.length === 1 ? rooms[0] : null;
};

const getRoomImages = (room, idx) => {
  if (!room) return [];
  const imgs = [];

  // Your Form.js saves: widthPreview / lengthPreview
  if (room.widthPreview) imgs.push({ url: room.widthPreview, alt: `Room ${idx + 1} width` });
  if (room.lengthPreview) imgs.push({ url: room.lengthPreview, alt: `Room ${idx + 1} length` });

  // Extra fallbacks (older docs)
  if (room.widthUrl) imgs.push({ url: room.widthUrl, alt: `Room ${idx + 1} width` });
  if (room.lengthUrl) imgs.push({ url: room.lengthUrl, alt: `Room ${idx + 1} length` });
  if (room.widthImageUrl) imgs.push({ url: room.widthImageUrl, alt: `Room ${idx + 1} width` });
  if (room.lengthImageUrl) imgs.push({ url: room.lengthImageUrl, alt: `Room ${idx + 1} length` });

  const seen = new Set();
  return imgs.filter((x) => x.url && !seen.has(x.url) && seen.add(x.url));
};

/* ---------------------------- numeric extractors ---------------------------- */

const numericWidth = (p) => {
  const t = getProjectType(p);

  // MultiRoom: only show width if exactly 1 room (otherwise misleading)
  if (t === "multiroom") {
    const r = getSingleRoom(p);
    if (!r) return null;

    const candidates = [r.aiWidth, r.aiWidthFt, r.widthFt, r.tmWidthFt, r.tapWidthFt];
    for (const v of candidates) {
      const n = toFiniteNumber(v);
      if (n != null) return n;
    }
    return null;
  }

  const candidates = [p.tmWidthFt, p.tapWidthFt, p.widthFt, p.aiWidthFt, p.aiWidth];
  for (const v of candidates) {
    const n = toFiniteNumber(v);
    if (n != null) return n;
  }
  return null;
};

const numericLength = (p) => {
  const t = getProjectType(p);

  if (t === "multiroom") {
    const r = getSingleRoom(p);
    if (!r) return null;

    const candidates = [r.aiLength, r.aiLengthFt, r.lengthFt, r.tmLengthFt, r.tapLengthFt];
    for (const v of candidates) {
      const n = toFiniteNumber(v);
      if (n != null) return n;
    }
    return null;
  }

  const candidates = [p.tmLengthFt, p.tapLengthFt, p.lengthFt, p.aiLengthFt, p.aiLength];
  for (const v of candidates) {
    const n = toFiniteNumber(v);
    if (n != null) return n;
  }
  return null;
};

const numericArea = (p) => {
  const t = getProjectType(p);

  if (t === "multiroom") {
    // 1) prefer summary.totalAiArea
    const sumFromSummary = toFiniteNumber(p?.summary?.totalAiArea);
    if (sumFromSummary != null) return sumFromSummary;

    // 2) top-level totalAreaSqFt
    const topLevel = toFiniteNumber(p?.totalAreaSqFt);
    if (topLevel != null) return topLevel;

    // 3) sum rooms[].aiArea
    if (Array.isArray(p.rooms) && p.rooms.length) {
      const sum = p.rooms.reduce((acc, r) => {
        const a =
          toFiniteNumber(r?.aiArea) ??
          toFiniteNumber(r?.areaSqFt) ??
          (() => {
            const w = toFiniteNumber(r?.aiWidth) ?? toFiniteNumber(r?.aiWidthFt);
            const l = toFiniteNumber(r?.aiLength) ?? toFiniteNumber(r?.aiLengthFt);
            return w != null && l != null ? w * l : null;
          })();

        return acc + (a ?? 0);
      }, 0);
      return sum > 0 ? sum : null;
    }

    return null;
  }

  // Surface/Room
  const area = toFiniteNumber(p?.areaSqFt);
  if (area != null) return area;

  const w = numericWidth(p);
  const l = numericLength(p);
  if (w != null && l != null) return w * l;

  return null;
};

const numericConfidencePct = (p) => {
  const aiC = toFiniteNumber(p.aiConfidence);
  if (aiC != null) return Math.round(aiC <= 1 ? aiC * 100 : aiC);

  const c = toFiniteNumber(p.confidence);
  if (c != null) return Math.round(c <= 1 ? c * 100 : c);

  const avg = toFiniteNumber(p?.summary?.avgConfidence);
  if (avg != null) return Math.round(avg);

  const roomAvg = Array.isArray(p?.rooms)
    ? Math.round(
        p.rooms
          .map((r) => toFiniteNumber(r?.confidencePct))
          .filter((x) => x != null)
          .reduce((a, b, _, arr) => a + b / arr.length, 0)
      )
    : null;

  return Number.isFinite(roomAvg) ? roomAvg : null;
};

const widthLabel = (p) => {
  const w = numericWidth(p);
  return typeof w === "number" ? w.toFixed(2) : "—";
};

const lengthLabel = (p) => {
  const l = numericLength(p);
  return typeof l === "number" ? l.toFixed(2) : "—";
};

const areaLabel = (p) => {
  const a = numericArea(p);
  return typeof a === "number" ? a.toFixed(2) : "—";
};

const confidenceLabel = (p) => {
  const c = numericConfidencePct(p);
  return typeof c === "number" ? `${c}%` : "—";
};

/* ------------------------------ auth/org helpers ------------------------------ */

async function requireAuthedUser() {
  const u = auth.currentUser;
  if (!u) throw new Error("Not authenticated");
  // warm token (also helps Firestore rules that rely on auth)
  await u.getIdToken(true);
  return u;
}

async function loadUserProfile(uid) {
  const snap = await getDoc(doc(db, USERS_COLLECTION, uid));
  return snap.exists() ? snap.data() : null;
}

async function loadOrgById(orgId) {
  if (!orgId) return null;

  // Try common collection names (first hit wins)
  for (const colName of ORG_COLLECTION_CANDIDATES) {
    try {
      const snap = await getDoc(doc(db, colName, orgId));
      if (snap.exists()) return { id: snap.id, _collection: colName, ...snap.data() };
    } catch {
      // ignore and continue
    }
  }
  return null;
}

function isSubscriptionActive(orgDoc) {
  const status = safeLower(orgDoc?.subscriptionStatus);
  // Treat trialing as allowed (you can tighten later)
  return status === "active" || status === "trialing";
}

/* ------------------------------ main component ------------------------------ */

export default function ContractorDashboard() {
  const navigate = useNavigate();

  // Gate state
  const [gateLoading, setGateLoading] = useState(true);
  const [gateError, setGateError] = useState(null);

  const [userProfile, setUserProfile] = useState(null);
  const [org, setOrg] = useState(null);
  const [isAllowed, setIsAllowed] = useState(false);

  // Existing dashboard state
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [homeownerEmail, setHomeownerEmail] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteStatus, setInviteStatus] = useState(null);
  // ✅ View Notes toggle (modal)
  const [showNotes, setShowNotes] = useState(false);
  useEffect(() => setShowNotes(false), [selected?.id]);

  // Filters
  const [filters, setFilters] = useState({
    type: "all", // all | surface | room | multiroom
    name: "",
    email: "",
    phone: "",
    minWidth: "",
    maxWidth: "",
    minLength: "",
    maxLength: "",
    minArea: "",
    maxArea: "",
    minConfidence: "",
    dateFrom: "",
    dateTo: "",
  });

  const [activeChip, setActiveChip] = useState("all");

  // Sorting
  const [sortConfig, setSortConfig] = useState({
    key: "timestamp",
    direction: "desc",
  });

  /* ------------------------------ PAYWALL ACTIONS ------------------------------ */

const startCheckout = async () => {
  try {
    setCreatingCheckout(true);

    if (!API_BASE) {
     alert("API base URL not configured. Check src/api/apiBase.js.");
      setCreatingCheckout(false);
      return;
    }

    const u = await requireAuthedUser();
    const idToken = await u.getIdToken();

    const payload = {
      contractorOrgId: userProfile?.contractorOrgId || null,
      email: u.email || null,
      plan: org?.plan || "base",
    };

    const res = await fetch(`${API_BASE}/api/stripe/create-checkout-session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      const msg =
        data?.error ||
        `Stripe checkout failed (${res.status}). Add backend route /api/stripe/create-checkout-session.`;
      alert(msg);
      setCreatingCheckout(false);
      return;
    }

    if (data?.url) {
      window.location.href = data.url;
      return;
    }

    alert("Checkout created but no redirect URL returned. Check backend response.");
    setCreatingCheckout(false);

  } catch (e) {
    console.error("Checkout error:", e);
    alert(e?.message || "Unable to start checkout.");
    setCreatingCheckout(false);
  }
};
  const openBillingPortal = async () => {
    try {
      if (!API_BASE) {
        alert("API base URL not configured. Check src/api/apiBase.js.");
        return;
      }

      const u = await requireAuthedUser();
      const idToken = await u.getIdToken();

      const res = await fetch(`${API_BASE}/api/stripe/create-billing-portal-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          contractorOrgId: userProfile?.contractorOrgId || null,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg =
          data?.error ||
          `Billing portal failed (${res.status}). Add backend route /api/stripe/create-billing-portal-session.`;
        alert(msg);
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
        return;
      }

      alert("Portal created but no redirect URL returned. Check backend response.");
    } catch (e) {
      console.error("Portal error:", e);
      alert(e?.message || "Unable to open billing portal.");
    }
  };

// ✅ ADD THIS RIGHT HERE
const handleSendInvite = async () => {
  try {
    setSendingInvite(true);
    setInviteStatus(null);

    const u = await requireAuthedUser();
    const idToken = await u.getIdToken();

    const res = await fetch(`${API_BASE}/api/contractor-invite`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`
      },
      body: JSON.stringify({
        contractorUid: u.uid,
        contractorEmail: u.email,
        homeownerEmail
      })
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.error || "Failed to send invite");
    }

    setInviteStatus("Invite sent successfully ✅");
    setHomeownerEmail("");

  } catch (err) {
    console.error("Invite error:", err);
    setInviteStatus("Failed to send invite ❌");
  } finally {
    setSendingInvite(false);
  }
};

  /* ------------------------------ SUBSCRIPTION GATE ------------------------------ */

  const runGate = async () => {
    setGateLoading(true);
    setGateError(null);

    try {
      const u = await requireAuthedUser();

      const profile = await loadUserProfile(u.uid);
      if (!profile) {
        throw new Error("No user profile found. Please sign out and create your account again.");
      }

      if (safeLower(profile.role) !== "contractor" && safeLower(profile.role) !== "admin") {
        // If you want admins to access contractor dashboard too, keep admin allowed.
        throw new Error("This account is not a contractor. Please sign in with a contractor account.");
      }

      const orgId = profile.contractorOrgId;
      if (!orgId) {
        throw new Error("This contractor is not linked to an organization (contractorOrgId missing).");
      }

      const orgDoc = await loadOrgById(orgId);
      if (!orgDoc) {
        throw new Error(
          `Organization not found for contractorOrgId=${orgId}. Check org collection name or document id.`
        );
      }

      setUserProfile(profile);
      setOrg(orgDoc);

      const allowed = isSubscriptionActive(orgDoc);
      setIsAllowed(allowed);
    } catch (e) {
      console.error("Contractor gate error:", e);
      setGateError(e?.message || "Access check failed.");
      setIsAllowed(false);
    } finally {
      setGateLoading(false);
    }
  };

  useEffect(() => {
    runGate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------------------ LOAD PROJECTS (only if allowed) ------------------------------ */

  const loadProjects = async () => {
    setLoading(true);
    setError(null);

    try {
      // Firestore (shared-only)
      let firestoreShared = [];
      try {
        const q = query(collection(db, "projects"), where("sharedWithContractor", "==", true));
        const snapshot = await getDocs(q);

        firestoreShared = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() || {};

          const normalizedEmail = data.email || data.ownerEmail || data.sharedByEmail || "—";
          const normalizedPhone = data.phone || "—";
          const normalizedTimestamp = data.timestamp || data.createdAt || data.sharedAt || null;

          return {
            id: docSnap.id,
            ...data,
            email: normalizedEmail,
            phone: normalizedPhone,
            timestamp: normalizedTimestamp,
          };
        });
      } catch (e) {
        console.warn("Firestore load skipped/failed (ok for now):", e?.message || e);
      }

      // localStorage fallback shared projects
      const localAll = safeParse(localStorage.getItem(LOCAL_STORAGE_KEY) || "[]", []);
      const localShared = localAll
        .filter((p) => p?.sharedWithContractor === true)
        .map((p) => {
          const inferredType = safeLower(p.projectType) || (Array.isArray(p.rooms) ? "multiroom" : "room");
          return {
            id: `local_${p.id}`,
            ...p,
            projectType: inferredType,
            timestamp: p.timestamp || p.createdAt || p.sharedAt || null,
            email: p.email || p.ownerEmail || p.sharedByEmail || "—",
            phone: p.phone || "—",
          };
        });

      const merged = [...firestoreShared, ...localShared];

      // newest first
      merged.sort((a, b) => {
        const am = toMillis(a.timestamp || a.createdAt || a.sharedAt);
        const bm = toMillis(b.timestamp || b.createdAt || b.sharedAt);
        return bm - am;
      });

      setProjects(merged);
    } catch (err) {
      console.error("❌ Error loading projects:", err);
      setError(err.message || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!gateLoading && isAllowed) loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateLoading, isAllowed]);

  const getTimestampMillis = (p) => toMillis(p?.timestamp || p?.createdAt || p?.sharedAt);

  const formatDate = (tsOrProject) => {
    const ms =
      typeof tsOrProject === "object" && tsOrProject !== null && !tsOrProject.toDate
        ? getTimestampMillis(tsOrProject)
        : toMillis(tsOrProject);

    if (!ms) return "Unknown";
    try {
      return new Date(ms).toLocaleString();
    } catch {
      return "Unknown";
    }
  };

  const clearAllFilters = () => {
    setFilters({
      type: "all",
      name: "",
      email: "",
      phone: "",
      minWidth: "",
      maxWidth: "",
      minLength: "",
      maxLength: "",
      minArea: "",
      maxArea: "",
      minConfidence: "",
      dateFrom: "",
      dateTo: "",
    });
    setActiveChip("all");
  };

  const applyChip = (chip) => {
    const now = Date.now();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
    const dateFromISO = new Date(sevenDaysAgo).toISOString().slice(0, 10);

    setActiveChip(chip);

    if (chip === "all") {
      clearAllFilters();
      return;
    }

    setFilters((prev) => {
      const base = { ...prev };
      switch (chip) {
        case "surface":
          base.type = "surface";
          break;
        case "room":
          base.type = "room";
          break;
        case "multiroom":
          base.type = "multiroom";
          break;
        case "recent":
          base.dateFrom = dateFromISO;
          break;
        case "highConfidence":
          base.minConfidence = "80";
          break;
        default:
          break;
      }
      return base;
    });
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      if (key === "timestamp") return { key, direction: "desc" };
      return { key, direction: "asc" };
    });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const filteredAndSortedProjects = useMemo(() => {
    let list = [...projects];

    // enforce shared-only always
    list = list.filter((p) => p?.sharedWithContractor === true);

    // filtering
    list = list.filter((p) => {
      const t = getProjectType(p);

      if (filters.type === "surface" && t !== "surface") return false;
      if (filters.type === "room" && t !== "room") return false;
      if (filters.type === "multiroom" && t !== "multiroom") return false;

      if (filters.name && !safeLower(nameLabel(p)).includes(safeLower(filters.name))) return false;
      if (filters.email && !safeLower(p.email).includes(safeLower(filters.email))) return false;
      if (filters.phone && !safeLower(p.phone).includes(safeLower(filters.phone))) return false;

      const w = numericWidth(p);
      if (filters.minWidth && typeof w === "number" && w < Number(filters.minWidth)) return false;
      if (filters.maxWidth && typeof w === "number" && w > Number(filters.maxWidth)) return false;

      const l = numericLength(p);
      if (filters.minLength && typeof l === "number" && l < Number(filters.minLength)) return false;
      if (filters.maxLength && typeof l === "number" && l > Number(filters.maxLength)) return false;

      const a = numericArea(p);
      if (filters.minArea && typeof a === "number" && a < Number(filters.minArea)) return false;
      if (filters.maxArea && typeof a === "number" && a > Number(filters.maxArea)) return false;

      const c = numericConfidencePct(p);
      if (filters.minConfidence && typeof c === "number" && c < Number(filters.minConfidence)) return false;

      const tsMillis = getTimestampMillis(p);
      if (filters.dateFrom) {
        const fromMillis = new Date(filters.dateFrom + "T00:00:00").getTime();
        if (tsMillis < fromMillis) return false;
      }
      if (filters.dateTo) {
        const toMillis2 = new Date(filters.dateTo + "T23:59:59").getTime();
        if (tsMillis > toMillis2) return false;
      }

      return true;
    });

    // sorting
    list.sort((a, b) => {
      const dir = sortConfig.direction === "asc" ? 1 : -1;
      const key = sortConfig.key;

      const getComparable = (p) => {
        switch (key) {
          case "type":
            return typeLabel(p);
          case "name":
            return nameLabel(p);
          case "email":
            return safeLower(p.email);
          case "phone":
            return safeLower(p.phone);
          case "width":
            return numericWidth(p) ?? -Infinity;
          case "length":
            return numericLength(p) ?? -Infinity;
          case "area":
            return numericArea(p) ?? -Infinity;
          case "confidence":
            return numericConfidencePct(p) ?? -Infinity;
          case "timestamp":
          default:
            return getTimestampMillis(p);
        }
      };

      const av = getComparable(a);
      const bv = getComparable(b);

      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });

    return list;
  }, [projects, filters, sortConfig]);

  const csvEscape = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
    return str;
  };

  const handleExportCsv = () => {
    if (!filteredAndSortedProjects.length) {
      alert("No shared projects to export for the current filters.");
      return;
    }

    const headers = [
      "Type",
      "Name",
      "HomeownerEmail",
      "Phone",
      "WidthFt",
      "LengthFt",
      "AreaSqFt",
      "ConfidencePct",
      "RoomsCount",
      "Date",
      "ProjectId",
      "SharedAt",
    ];

    const rows = filteredAndSortedProjects.map((p) => {
      const t = getProjectType(p);
      const w = numericWidth(p);
      const l = numericLength(p);
      const a = numericArea(p);
      const c = numericConfidencePct(p);
      const roomsCount = t === "multiroom" ? (Array.isArray(p.rooms) ? p.rooms.length : "") : "";

      return [
        typeLabel(p),
        nameLabel(p),
        p.email || "",
        p.phone || "",
        typeof w === "number" ? w.toFixed(2) : "",
        typeof l === "number" ? l.toFixed(2) : "",
        typeof a === "number" ? a.toFixed(2) : "",
        typeof c === "number" ? c : "",
        roomsCount,
        formatDate(p),
        p.id,
        formatDate(p.sharedAt),
      ];
    });

    const csvLines = [headers.map(csvEscape).join(","), ...rows.map((r) => r.map(csvEscape).join(","))];
    const blob = new Blob([csvLines.join("\r\n")], { type: "text/csv;charset=utf-8;" });

    const now = new Date();
    const stamp = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, "0"),
      String(now.getDate()).padStart(2, "0"),
      "-",
      String(now.getHours()).padStart(2, "0"),
      String(now.getMinutes()).padStart(2, "0"),
    ].join("");

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tile-ai-shared-projects-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /* --------------------------------- render --------------------------------- */

  // 1) Gate loading
  if (gateLoading) {
    return <div className="contractor-loading">Checking subscription…</div>;
  }

  // 2) Gate error
  if (gateError) {
    return (
      <div className="contractor-dashboard">
        <header className="dashboard-header">
          <div>
            <h1>Contractor Project Dashboard</h1>
            <p>Subscription check failed.</p>
          </div>
        </header>

        <p style={{ color: "red", marginTop: "1rem", fontWeight: 800 }}>
          {gateError}
        </p>

        <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
          <button className="retry-btn" onClick={runGate}>
            Retry
          </button>
          <button className="retry-btn" onClick={() => navigate("/measure")}>
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // 3) Paywall (inactive)
  if (!isAllowed) {
    const orgName = org?.name || "Your company";
    const status = prettyStatus(org?.subscriptionStatus);

    return (
      <div className="contractor-dashboard">
        <header className="dashboard-header">
          <div>
            <h1>Contractor Access Required</h1>
            <p style={{ maxWidth: 720 }}>
              Your contractor organization <strong>{orgName}</strong> does not have an active
              subscription. Activate the <strong>{org?.plan || "base"}</strong> plan to access shared
              homeowner projects.
            </p>
          </div>
        </header>

        <div
          style={{
            marginTop: 16,
            padding: 16,
            borderRadius: 14,
            border: "1px solid rgba(17,24,39,0.12)",
            background: "rgba(243,247,255,0.45)",
            maxWidth: 860,
          }}
        >
          <div style={{ fontWeight: 900, color: "#0b3a6a", marginBottom: 6 }}>
            Subscription status: <span style={{ color: "#111827" }}>{status}</span>
          </div>

          <div style={{ fontWeight: 800, color: "rgba(17,24,39,0.75)" }}>
            Contractor: {userProfile?.email || auth.currentUser?.email || "—"}
            <br />
            Org ID: {userProfile?.contractorOrgId || "—"}
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
           <button
  className="export-btn"
  onClick={startCheckout}
  disabled={creatingCheckout}
>
  {creatingCheckout
    ? "Preparing your secure checkout. Please wait a few seconds..."
    : "Activate / Subscribe"}
</button>
            <button className="clear-filters-btn" onClick={openBillingPortal}>
              Manage billing
            </button>

            <button className="clear-filters-btn" onClick={runGate}>
              Refresh status
            </button>
          </div>

          <div style={{ marginTop: 12, fontSize: 13, color: "rgba(17,24,39,0.72)", lineHeight: 1.5 }}>
            Note: The “Activate / Subscribe” button will work after you add backend routes for Stripe
            checkout + billing portal. Until then, it will alert you with what endpoint is missing.
          </div>
        </div>
      </div>
    );
  }

  // 4) Allowed: show dashboard loading/error as before
  if (loading) return <div className="contractor-loading">Loading…</div>;

  if (error) {
    return (
      <div className="contractor-dashboard">
        <header className="dashboard-header">
          <div>
            <h1>Contractor Project Dashboard</h1>
            <p>Shared homeowner projects from MeasureSmart AI.</p>
          </div>
        </header>
        <p style={{ color: "red", marginTop: "1rem" }}>Error loading projects: {error}</p>
        <button className="retry-btn" onClick={loadProjects}>
          Retry
        </button>
      </div>
    );
  }
  // --- Subscription badge state ---
const subscriptionStatus = prettyStatus(org?.subscriptionStatus);
const subscriptionRaw = safeLower(org?.subscriptionStatus);
const renewalDate =
  org?.currentPeriodEnd
    ? new Date(org.currentPeriodEnd).toLocaleDateString()
    : null;
  return (
    <div className="contractor-dashboard">
      <header className="dashboard-header">
        <div>
          <h1>Contractor Project Dashboard</h1>
          <p>Only projects explicitly shared by homeowners appear here.</p>
        </div>
  <div className="dashboard-header-meta">
  <div className={`subscription-badge subscription-${subscriptionRaw || "inactive"}`}>
    {subscriptionStatus === "active" && (
      <>
        Active Subscription
        {renewalDate && (
          <span className="subscription-renewal">
            {" "}• Renews {renewalDate}
          </span>
        )}
      </>
    )}

    {subscriptionStatus === "trialing" && (() => {
  const trialEnd = org?.trialEndsAt?.toDate
    ? org.trialEndsAt.toDate()
    : org?.trialEndsAt
    ? new Date(org.trialEndsAt)
    : null;

  const daysLeft = trialEnd
    ? Math.max(0, Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <>
      Free Trial
      {daysLeft !== null && (
        <span className="subscription-renewal">
          {" "}• {daysLeft} day{daysLeft !== 1 ? "s" : ""} remaining
        </span>
      )}
    </>
  );
})()}

    {subscriptionStatus === "past due" && "Past Due"}
    {subscriptionStatus === "canceled" && "Canceled"}
  </div>

  <span>
    Shared projects: <strong>{filteredAndSortedProjects.length}</strong>
  </span>
</div>
      </header>

<section style={{
  background: "#f5f7fb",
  padding: "16px",
  borderRadius: "12px",
  marginBottom: "16px"
}}>
  <h3 style={{ marginBottom: "10px" }}>Invite Homeowner</h3>

  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
    <input
      type="email"
      placeholder="Enter homeowner email"
      value={homeownerEmail}
      onChange={(e) => setHomeownerEmail(e.target.value)}
      style={{
        padding: "10px",
        flex: "1",
        minWidth: "250px",
        borderRadius: "6px",
        border: "1px solid #ccc"
      }}
    />

    <button
      onClick={handleSendInvite}
      disabled={sendingInvite || !homeownerEmail}
      className="export-btn"
    >
      {sendingInvite ? "Sending..." : "Send Invite"}
    </button>
  </div>

  {inviteStatus && (
    <div style={{ marginTop: "10px", fontWeight: "600" }}>
      {inviteStatus}
    </div>
  )}
</section>
      <section className="contractor-filter-bar">
        <div className="filter-chips">
          <button className={`filter-chip ${activeChip === "all" ? "chip-active" : ""}`} onClick={() => applyChip("all")}>
            All shared
          </button>
          <button className={`filter-chip ${activeChip === "surface" ? "chip-active" : ""}`} onClick={() => applyChip("surface")}>
            Surface only
          </button>
          <button className={`filter-chip ${activeChip === "room" ? "chip-active" : ""}`} onClick={() => applyChip("room")}>
            Rooms only
          </button>
          <button className={`filter-chip ${activeChip === "multiroom" ? "chip-active" : ""}`} onClick={() => applyChip("multiroom")}>
            MultiRoom only
          </button>
          <button className={`filter-chip ${activeChip === "recent" ? "chip-active" : ""}`} onClick={() => applyChip("recent")}>
            Last 7 days
          </button>
          <button className={`filter-chip ${activeChip === "highConfidence" ? "chip-active" : ""}`} onClick={() => applyChip("highConfidence")}>
            High confidence (≥ 80%)
          </button>
        </div>

        <div className="filter-actions">
          <button className="export-btn" onClick={handleExportCsv}>
            Export CSV
          </button>
          <button className="clear-filters-btn" onClick={clearAllFilters}>
            Clear all filters
          </button>
        </div>
      </section>

      <div className="project-list">
        {filteredAndSortedProjects.length === 0 ? (
          <p className="no-projects">No shared projects yet. A homeowner must click “Share with Contractor”.</p>
        ) : (
          <div className="table-wrapper">
            <table className="project-table">
              <thead>
                <tr>
                  <th>
                    <button type="button" className="th-sort-button" onClick={() => handleSort("type")}>
                      Type <span className="sort-indicator">{getSortIndicator("type")}</span>
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort-button" onClick={() => handleSort("name")}>
                      Name <span className="sort-indicator">{getSortIndicator("name")}</span>
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort-button" onClick={() => handleSort("email")}>
                      Homeowner <span className="sort-indicator">{getSortIndicator("email")}</span>
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort-button" onClick={() => handleSort("phone")}>
                      Phone <span className="sort-indicator">{getSortIndicator("phone")}</span>
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort-button" onClick={() => handleSort("width")}>
                      Width (ft) <span className="sort-indicator">{getSortIndicator("width")}</span>
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort-button" onClick={() => handleSort("length")}>
                      Length (ft) <span className="sort-indicator">{getSortIndicator("length")}</span>
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort-button" onClick={() => handleSort("area")}>
                      Area (sq ft) <span className="sort-indicator">{getSortIndicator("area")}</span>
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort-button" onClick={() => handleSort("confidence")}>
                      Confidence <span className="sort-indicator">{getSortIndicator("confidence")}</span>
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort-button" onClick={() => handleSort("timestamp")}>
                      Date <span className="sort-indicator">{getSortIndicator("timestamp")}</span>
                    </button>
                  </th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {filteredAndSortedProjects.map((p) => {
                  const t = getProjectType(p);
                  const chipClass =
                    t === "surface"
                      ? "project-type-surface"
                      : t === "multiroom"
                      ? "project-type-multiroom"
                      : "project-type-room";

                  return (
                    <tr key={p.id}>
                      <td>
                        <span className={`project-type-chip ${chipClass}`}>{typeLabel(p)}</span>
                      </td>
                      <td title={nameLabel(p)}>{nameLabel(p)}</td>
                      <td>{p.email || "—"}</td>
                      <td>{p.phone || "—"}</td>
                      <td>{widthLabel(p)}</td>
                      <td>{lengthLabel(p)}</td>
                      <td>{areaLabel(p)}</td>
                      <td>{confidenceLabel(p)}</td>
                      <td>{formatDate(p)}</td>
                      <td>
                        <button className="view-btn" onClick={() => setSelected(p)}>
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {filteredAndSortedProjects.length === 0 && (
                  <tr>
                    <td colSpan={10} className="no-results-row">
                      No shared projects match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="modal-backdrop">
          <div
            className="modal-content"
            style={{
              maxHeight: "85vh",
              overflowY: "auto",
            }}
          >
            <h2>
              {typeLabel(selected)} – {nameLabel(selected)}
            </h2>

            <p>
              <strong>Homeowner:</strong> {selected.email || "—"}
              <br />
              <strong>Phone:</strong> {selected.phone || "—"}
              <br />
              <strong>Date:</strong> {formatDate(selected)}
              <br />
              <strong>Shared:</strong> {formatDate(selected.sharedAt)}
            </p>

            {/* ✅ View Notes (compact, optional) */}
            {Boolean(selected.surfaceName || selected.notes) && (
              <div style={{ margin: "10px 0 2px" }}>
                <button
                  type="button"
                  className="view-btn"
                  onClick={() => setShowNotes((v) => !v)}
                  style={{ padding: "8px 12px" }}
                >
                  {showNotes ? "Hide Notes" : "View Notes"}
                </button>

                {showNotes && (
                  <div
                    style={{
                      marginTop: 10,
                      padding: "12px 12px",
                      border: "1px solid #e3e9f7",
                      borderRadius: 10,
                      background: "#f7fbff",
                      color: "#183a5a",
                    }}
                  >
                    {selected.surfaceName && (
                      <div style={{ marginBottom: selected.notes ? 10 : 0 }}>
                        <strong>Surface / Area:</strong> {selected.surfaceName}
                      </div>
                    )}

                    {selected.notes && (
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        <strong>Notes:</strong> {selected.notes}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {getProjectType(selected) === "multiroom" ? (
              <>
                <p>
                  <strong>Total Area:</strong>{" "}
                  {typeof numericArea(selected) === "number" ? `${numericArea(selected).toFixed(2)} sq ft` : "—"}
                  <br />
                  <strong>Rooms:</strong> {Array.isArray(selected.rooms) ? selected.rooms.length : "—"}
                  <br />
                  <strong>Width:</strong> {widthLabel(selected)}
                  <br />
                  <strong>Length:</strong> {lengthLabel(selected)}
                  <br />
                  <strong>Avg Confidence:</strong> {confidenceLabel(selected)}
                </p>

                {/* Render each room separately */}
                {Array.isArray(selected.rooms) && selected.rooms.length > 0 ? (
                  selected.rooms.map((r, idx) => {
                    const imgs = getRoomImages(r, idx);
                    const w = toFiniteNumber(r?.aiWidth) ?? toFiniteNumber(r?.aiWidthFt);
                    const l = toFiniteNumber(r?.aiLength) ?? toFiniteNumber(r?.aiLengthFt);
                    const a = toFiniteNumber(r?.aiArea) ?? (w != null && l != null ? w * l : null);
                    const c = toFiniteNumber(r?.confidencePct);

                    return (
                      <div
                        key={r?.id || `${selected.id}_room_${idx}`}
                        style={{
                          marginTop: 18,
                          paddingTop: 12,
                          borderTop: "1px solid rgba(17,24,39,0.12)",
                        }}
                      >
                        <div style={{ fontWeight: 900, color: "#0b3a6a", marginBottom: 8 }}>
                          Room {idx + 1}
                        </div>

                        <div className="modal-images">
                          {imgs.length ? (
                            imgs.map((im, i) => <img key={`${idx}_${i}`} src={im.url} alt={im.alt} />)
                          ) : (
                            <div style={{ color: "#777", fontWeight: 700 }}>No images saved for this room.</div>
                          )}
                        </div>

                        <p style={{ marginTop: 10 }}>
                          <strong>Width:</strong> {typeof w === "number" ? `${w.toFixed(2)} ft` : "—"}
                          <br />
                          <strong>Length:</strong> {typeof l === "number" ? `${l.toFixed(2)} ft` : "—"}
                          <br />
                          <strong>Area:</strong> {typeof a === "number" ? `${a.toFixed(2)} sq ft` : "—"}
                          <br />
                          <strong>Confidence:</strong> {typeof c === "number" ? `${Math.round(c)}%` : "—"}
                        </p>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ color: "#777", fontWeight: 700 }}>No room data found on this project.</div>
                )}
              </>
            ) : getProjectType(selected) === "surface" ? (
              <>
                <div className="modal-images">
                  {selected.surfaceUrl ? (
                    <img src={selected.surfaceUrl} alt="Surface" />
                  ) : (
                    <div style={{ color: "#777", fontWeight: 700 }}>No surface image saved.</div>
                  )}
                </div>

                <p>
                  <strong>Width:</strong>{" "}
                  {typeof selected.aiWidthFt === "number" ? `${selected.aiWidthFt.toFixed(2)} ft` : "—"}
                  <br />
                  <strong>Length:</strong>{" "}
                  {typeof selected.aiLengthFt === "number" ? `${selected.aiLengthFt.toFixed(2)} ft` : "—"}
                  <br />
                  <strong>Area:</strong>{" "}
                  {typeof selected.areaSqFt === "number" ? `${selected.areaSqFt.toFixed(2)} sq ft` : "—"}
                </p>
              </>
            ) : (
              <>
                <div className="modal-images">
                  {getTopLevelImages(selected).length ? (
                    getTopLevelImages(selected).map((im, i) => <img key={i} src={im.url} alt={im.alt} />)
                  ) : (
                    <div style={{ color: "#777", fontWeight: 700 }}>No images saved for this project.</div>
                  )}
                </div>

                <p>
                  <strong>Width:</strong> {widthLabel(selected)} ft
                  <br />
                  <strong>Length:</strong> {lengthLabel(selected)} ft
                  <br />
                  <strong>Area:</strong> {areaLabel(selected)} sq ft
                  <br />
                  <strong>AI Confidence:</strong> {confidenceLabel(selected)}
                </p>
              </>
            )}

            <button className="close-btn" onClick={() => setSelected(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
